// ChangeMarker — the gutter read-wear mark at an element, pure.
//
// .claude/research/design/03: each changed element carries a small marker in the margin;
// clicking it opens the review control there. .claude/research/design/04: evidence and issue
// markers live in the gutter too — a quiet glanceable read-wear that escalates
// to the side surface only on demand (inline marker → hover → panel).
//
// One `GutterMarker` discriminant, THREE distinct looks that must never look
// alike (alarm discipline, .claude/research/design/04):
//   • proposal — an agent proposal pending here, in the agent colour.
//   • issue    — a diagnostic: blocked (a failing Rule) reads LOUD/danger;
//                warning (a Check) reads QUIET/warning. Split by severity so a
//                blocking issue never looks like an advisory one.
//   • grounding — the claim's grounding read-wear, borrowing the chip's three-
//                state colour so the gutter and the chip agree.
//
// It is a real control (click → open the review/inspection), so it meets the
// 24px target floor and carries a caller-localised accessible name. Colour is
// never the sole channel: each look pairs its tint with a distinct glyph shape.
//
// Pure/props-driven: `marker` picks the look, `label` names it for AT, activation
// reports UP. No state, no I/O.

import { useLingui } from "@lingui/react/macro";
import type { GroundingState } from "../../Grounding/types.js";
import { Icon } from "../../Icon/Icon.js";
import type { GutterMarker, IssueSeverity } from "../types.js";
import "./ChangeMarker.css";

export type ChangeMarkerProps = {
    readonly marker: GutterMarker;
    // Whether the review/inspection surface anchored to this marker is open —
    // reflected as aria-expanded and a pressed look. Controlled by the parent.
    readonly expanded?: boolean;
    readonly onActivate?: () => void;
};

const proposalGlyph = <Icon name="pencil" />;

const issueGlyph = <Icon name="warning" />;

const groundingGlyph = <Icon name="anchor" />;

const groundingModifier = (state: GroundingState): string => {
    switch (state.type) {
        case "grounded":
            return "bilda-change-marker--grounded";
        case "weak":
            return "bilda-change-marker--weak";
        case "ungrounded":
        case "unsupported":
            return "bilda-change-marker--ungrounded";
        default: {
            const exhaustive: never = state;
            return exhaustive;
        }
    }
};

const issueModifier = (severity: IssueSeverity): string =>
    severity === "blocked"
        ? "bilda-change-marker--blocked"
        : "bilda-change-marker--warning";

export const ChangeMarker = ({
    marker,
    expanded,
    onActivate,
}: ChangeMarkerProps) => {
    const { t } = useLingui();
    // Per-variant look + accessible name. A match keeps each concrete; the
    // grounding state and issue severity pick the sub-look without a flag.
    const view =
        marker.type === "proposal"
            ? {
                  glyph: proposalGlyph,
                  cls: "bilda-change-marker--proposal",
                  label: t`Review the proposed change`,
              }
            : marker.type === "issue"
              ? {
                    glyph: issueGlyph,
                    cls: issueModifier(marker.severity),
                    label:
                        marker.severity === "blocked"
                            ? t`Blocking issue — must fix`
                            : t`Advisory — worth a look`,
                }
              : {
                    glyph: groundingGlyph,
                    cls: groundingModifier(marker.state),
                    label:
                        marker.state.type === "unsupported"
                            ? t`Unsupported`
                            : t`Inspect the evidence`,
                };

    return (
        <button
            type="button"
            className={`bilda-change-marker ${view.cls}`}
            aria-label={view.label}
            aria-expanded={expanded ?? false}
            onClick={onActivate}
        >
            {view.glyph}
        </button>
    );
};
